import React, { useState, useEffect } from "react";
import Cookies from 'js-cookie';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    Typography,
    Pagination,
} from "@mui/material";
import HistoryIcon from '@mui/icons-material/History';

const FIELD_LABELS = {
    title: "Title",
    recipe_description: "Description",
    prep_time: "Prep Time",
    recipe_yield: "Yield",
    author: "Author",
};

const IMG_STYLE = { width: 120, height: 120, borderRadius: 4, objectFit: "cover", display: "block" };

const formatIngredient = (ing) =>
    [ing.quantity, ing.unit, ing.ingredient].filter(Boolean).join(" ");

const renderDifferences = (pre, post, changeSummary) => {
    if (!pre || !post) return <Typography>No comparison available.</Typography>;

    if (changeSummary === "reorder" || changeSummary === "reorder-items") {
        return (
            <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
                Ingredients or directions were reordered within their sections.
            </Typography>
        );
    }

    if (changeSummary?.startsWith("reorder-sections:")) {
        let meta = {};
        try { meta = JSON.parse(changeSummary.slice("reorder-sections:".length)); } catch {}
        const arrow = (before, after) => (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {before.map((s, i) => <Chip key={i} label={s || '(unnamed)'} size="small" sx={{ bgcolor: 'error.light', color: 'white' }} />)}
                </Box>
                <Typography variant="body2" color="text.secondary">→</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {after.map((s, i) => <Chip key={i} label={s || '(unnamed)'} size="small" sx={{ bgcolor: 'success.light', color: 'white' }} />)}
                </Box>
            </Box>
        );
        return (
            <Box>
                {meta.ing_sections_before && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Ingredient Sections Reordered</Typography>
                        {arrow(meta.ing_sections_before, meta.ing_sections_after)}
                    </Box>
                )}
                {meta.dir_sections_before && (
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Direction Sections Reordered</Typography>
                        {arrow(meta.dir_sections_before, meta.dir_sections_after)}
                    </Box>
                )}
            </Box>
        );
    }

    const sections = [];

    // --- Scalar fields ---
    const fieldChanges = [];
    for (const [key, label] of Object.entries(FIELD_LABELS)) {
        const before = pre[key] ?? null;
        const after = post[key] ?? null;
        if (before !== after) {
            fieldChanges.push({ label, before, after });
        }
    }
    if (fieldChanges.length > 0) {
        sections.push(
            <Box key="fields" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                    Recipe Details
                </Typography>
                {fieldChanges.map(({ label, before, after }) => (
                    <Box key={label} sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                        {before != null && (
                            <Typography variant="body2" sx={{ textDecoration: "line-through", color: "error.main" }}>
                                {before}
                            </Typography>
                        )}
                        {after != null && (
                            <Typography variant="body2" sx={{ color: "success.main" }}>
                                {after}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        );
    }

    // --- Image ---
    if (pre.download_image_url !== post.download_image_url) {
        sections.push(
            <Box key="image" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                    Image
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {pre.download_image_url && (
                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="caption" color="text.secondary">Before</Typography>
                            <img src={pre.download_image_url} alt="before" style={IMG_STYLE} />
                        </Box>
                    )}
                    <Typography variant="h5" color="text.secondary">→</Typography>
                    {post.download_image_url && (
                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="caption" color="text.secondary">After</Typography>
                            <img src={post.download_image_url} alt="after" style={IMG_STYLE} />
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    // --- Ingredients ---
    const preIngs = (pre.ingredients || []).map(formatIngredient);
    const postIngs = (post.ingredients || []).map(formatIngredient);
    const addedIngs = postIngs.filter(i => !preIngs.includes(i));
    const removedIngs = preIngs.filter(i => !postIngs.includes(i));
    if (addedIngs.length > 0 || removedIngs.length > 0) {
        sections.push(
            <Box key="ingredients" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                    Ingredients
                </Typography>
                {removedIngs.map((ing, i) => (
                    <Chip key={`rem-${i}`} label={`− ${ing}`} size="small"
                        sx={{ mr: 0.5, mb: 0.5, bgcolor: "error.light", color: "white" }} />
                ))}
                {addedIngs.map((ing, i) => (
                    <Chip key={`add-${i}`} label={`+ ${ing}`} size="small"
                        sx={{ mr: 0.5, mb: 0.5, bgcolor: "success.light", color: "white" }} />
                ))}
            </Box>
        );
    }

    // --- Directions ---
    const preDirs = (pre.directions || [])
        .slice().sort((a, b) => (a.direction_order ?? 0) - (b.direction_order ?? 0))
        .map(d => d.direction);
    const postDirs = (post.directions || [])
        .slice().sort((a, b) => (a.direction_order ?? 0) - (b.direction_order ?? 0))
        .map(d => d.direction);
    const addedDirs = postDirs.filter(d => !preDirs.includes(d));
    const removedDirs = preDirs.filter(d => !postDirs.includes(d));
    if (addedDirs.length > 0 || removedDirs.length > 0) {
        sections.push(
            <Box key="directions" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                    Directions
                </Typography>
                {removedDirs.map((dir, i) => (
                    <Typography key={`rem-${i}`} variant="body2"
                        sx={{ textDecoration: "line-through", color: "error.main", mb: 0.5 }}>
                        {dir}
                    </Typography>
                ))}
                {addedDirs.map((dir, i) => (
                    <Typography key={`add-${i}`} variant="body2"
                        sx={{ color: "success.main", mb: 0.5 }}>
                        {dir}
                    </Typography>
                ))}
            </Box>
        );
    }

    if (sections.length === 0) {
        return <Typography color="text.secondary">No changes detected.</Typography>;
    }

    return <Box>{sections}</Box>;
};

const PAGE_SIZE = 5;

// Backend returns naive UTC datetimes without a Z suffix. Appending Z forces
// JavaScript to interpret them as UTC before converting to the display timezone.
const formatDate = (dateStr) =>
    new Date(dateStr + 'Z').toLocaleString("en-US", { timeZone: "America/Chicago" });

const RecipeHistory = ({ open, onClose, recipeId }) => {
    const [versions, setVersions] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewVersion, setPreviewVersion] = useState(null);

    useEffect(() => {
        if (open) {
            setPage(1);
        } else {
            setPreviewVersion(null);
        }
    }, [open, recipeId]);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setError(null);
        const fetchVersions = async () => {
            try {
                const api = `${process.env.REACT_APP_BACKEND_ADDRESS}/recipe/${recipeId}/versions?page=${page}&page_size=${PAGE_SIZE}`;
                const response = await fetch(api, {
                    method: 'GET',
                    headers: { 'Authorization': 'Bearer ' + Cookies.get('token') },
                });
                if (!response.ok) throw new Error("Failed to fetch version history");
                const data = await response.json();
                setVersions(data.versions);
                setTotal(data.total);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchVersions();
    }, [open, recipeId, page]);

    return (
        <>
            {/* Version list dialog */}
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><HistoryIcon sx={{ color: 'primary.main' }} />Version History</DialogTitle>
                <DialogContent>
                    {loading ? (
                        <CircularProgress />
                    ) : error ? (
                        <Typography color="error">{error}</Typography>
                    ) : versions.length === 0 ? (
                        <Typography color="text.secondary">No history available.</Typography>
                    ) : (
                        <>
                            <List disablePadding>
                                {versions.map((version) => (
                                    <React.Fragment key={version.pre_version.id}>
                                        <ListItem sx={{ px: 0 }}>
                                            <Grid container alignItems="center">
                                                <Grid item xs={8}>
                                                    <ListItemText
                                                        primary={version.pre_version.first_name || version.pre_version.last_name ? `${version.pre_version.first_name ?? ""} ${version.pre_version.last_name ?? ""}`.trim() : "Unknown user"}
                                                        secondary={formatDate(version.pre_version.date_created)}
                                                    />
                                                </Grid>
                                                <Grid item xs={4} sx={{ textAlign: "right" }}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => setPreviewVersion(version)}
                                                    >
                                                        View changes
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </ListItem>
                                        <Divider />
                                    </React.Fragment>
                                ))}
                            </List>
                            {total > PAGE_SIZE && (
                                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                                    <Pagination
                                        count={Math.ceil(total / PAGE_SIZE)}
                                        page={page}
                                        onChange={(_, value) => setPage(value)}
                                        size="small"
                                        color="primary"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Change detail dialog */}
            {previewVersion && (
                <Dialog open={true} onClose={() => setPreviewVersion(null)} fullWidth maxWidth="sm">
                    <DialogTitle>
                        Changes on {formatDate(previewVersion.pre_version.date_created)}
                    </DialogTitle>
                    <DialogContent>
                        {renderDifferences(
                            previewVersion.pre_version.snapshot,
                            previewVersion.post_version.snapshot,
                            previewVersion.pre_version.change_summary,
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

export default RecipeHistory;
